import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { CalcConfigService } from '_services/calc-config.service';
import { UnivPrefsService } from '_services/univ-prefs.service';
import { MatCard, MatCardHeader, MatCardTitle, MatCardContent } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatFormField } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatButton } from '@angular/material/button';

interface PackagingConfigValue {
  showEFC: boolean;
  useRounding: boolean;
  roundingValue: number;
  efcRounding: boolean;
}

@Component({
  selector: 'app-packaging-config',
  templateUrl: './packaging-config.component.html',
  styleUrls: ['./packaging-config.component.css'],
  imports: [
    ReactiveFormsModule,
    MatCard, MatCardHeader, MatCardTitle, MatCardContent,
    MatCheckbox, MatFormField, MatSelect, MatOption, MatButton,
  ],
})
export class PackagingConfigComponent implements OnInit {
  configForm: UntypedFormGroup;
  state: 'loading' | 'synced' | 'modified' | 'error' = 'loading';
  displaySettingLabel = 'Show SAI above results';
  schoolId: string;

  private savedValue: PackagingConfigValue | null = null;
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private fb: UntypedFormBuilder,
    private cc: CalcConfigService,
    private ups: UnivPrefsService,
    private route: ActivatedRoute,
  ) {
    this.configForm = this.fb.group({
      showEFC: [false],
      useRounding: [false],
      roundingValue: [{ value: 50, disabled: true }],
      efcRounding: [false],
    });
    this.schoolId = this.route.snapshot.parent?.paramMap.get('schoolId') ?? '';
  }

  ngOnInit() {
    this.cc.institutionalMethod$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(isIm => {
      this.displaySettingLabel = isIm ? 'Show Family Contribution above results' : 'Show SAI above results';
    });

    this.ups.draftPrefs.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.tryPatch());

    this.cc.draftRules.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.tryPatch());

    this.configForm.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.syncRoundingValueEnabled();
      if (this.state !== 'loading') {
        this.state = 'modified';
      }
    });

    this.tryPatch();
  }

  async save() {
    if (!this.configForm.valid || (this.state !== 'modified' && this.state !== 'error')) {
      return;
    }

    const value = this.formValue();
    try {
      await this.ups.savePreferences({ showEFC: value.showEFC }, true);
      await this.ups.savePreferences({ showEFC: value.showEFC }, false);
      await this.cc.saveDraftRules(this.schoolId, {
        useRounding: value.useRounding,
        efcRounding: value.efcRounding,
        roundingValue: value.roundingValue,
      });
      this.savedValue = value;
      this.configForm.markAsPristine();
      this.state = 'synced';
    } catch (err) {
      console.log(err);
      this.state = 'error';
    }
  }

  cancel() {
    if (!this.savedValue || (this.state !== 'modified' && this.state !== 'error')) {
      return;
    }
    this.configForm.patchValue(this.savedValue, { emitEvent: false });
    this.syncRoundingValueEnabled();
    this.configForm.markAsPristine();
    this.state = 'synced';
  }

  private tryPatch(allowRetry = true) {
    if (this.state === 'modified' || this.state === 'error') {
      return;
    }
    // Draft rules publish their value before loadedDraftSchoolId is assigned.
    if (this.ups.loadedSchoolId !== this.schoolId || this.cc.loadedDraftSchoolId !== this.schoolId) {
      if (allowRetry) {
        queueMicrotask(() => this.tryPatch(false));
      }
      return;
    }
    const prefs = this.ups.draftPrefs.value;
    const rules = this.cc.draftRules.value;
    if (!prefs || !rules) {
      return;
    }

    const value: PackagingConfigValue = {
      showEFC: !!prefs.showEFC,
      useRounding: !!rules.useRounding,
      roundingValue: this.normalizeRoundingValue(rules.roundingValue),
      efcRounding: !!rules.efcRounding,
    };
    this.savedValue = value;
    this.configForm.patchValue(value, { emitEvent: false });
    this.syncRoundingValueEnabled();
    this.configForm.markAsPristine();
    this.state = 'synced';
  }

  private syncRoundingValueEnabled() {
    const rounding = this.configForm.controls['roundingValue'];
    const enabled = !!this.configForm.controls['useRounding'].value || !!this.configForm.controls['efcRounding'].value;
    if (enabled && rounding.disabled) {
      rounding.enable({ emitEvent: false });
    } else if (!enabled && rounding.enabled) {
      rounding.disable({ emitEvent: false });
    }
  }

  private formValue(): PackagingConfigValue {
    const raw = this.configForm.getRawValue();
    return {
      showEFC: !!raw.showEFC,
      useRounding: !!raw.useRounding,
      roundingValue: this.normalizeRoundingValue(raw.roundingValue),
      efcRounding: !!raw.efcRounding,
    };
  }

  private normalizeRoundingValue(value: unknown): number {
    const roundingValue = Number(value);
    return roundingValue === 50 || roundingValue === 100 || roundingValue === 200 || roundingValue === 500
      ? roundingValue
      : 50;
  }
}
